import type {
  ApiError,
  ChangeRequest,
  ConfirmAssignmentRequest,
  CreateChangeRequest,
  CreateDepartmentRequest,
  CreateHolidayRequest,
  CreateShiftTemplateRequest,
  CreateSubDepartmentRequest,
  CreateTimeOffRequest,
  DecideChangeRequest,
  DecideTimeOffRequest,
  Department,
  GetBillingResponse,
  PatchOrgBillingRequest,
  PatchUserBillingRequest,
  GenerateScheduleRequest,
  GenerateScheduleResponse,
  HealthResponse,
  Holiday,
  ListAuditLogResponse,
  ListHolidaysResponse,
  ListChangeRequestsResponse,
  ListDayUnavailabilityResponse,
  ListDepartmentsResponse,
  ListNotificationsResponse,
  ListOpenShiftsResponse,
  ListPreferencesResponse,
  ListShiftTemplatesResponse,
  ListShiftsResponse,
  ListSubDepartmentsResponse,
  ListTimeOffResponse,
  ListUsersResponse,
  MeResponse,
  PatchDepartmentRequest,
  PatchHolidayRequest,
  PatchShiftAssignmentsRequest,
  PatchShiftTemplateRequest,
  PatchSubDepartmentRequest,
  PatchSettingsRequest,
  PatchUserRequest,
  Preference,
  PreferenceState,
  PublishScheduleRequest,
  GetSettingsResponse,
  RespondChangeRequest,
  ShiftTemplate,
  ShiftWithAssignments,
  SubDepartment,
  TimeOffRequest,
  UpdateMeRequest,
  UserWithMemberships,
} from "@scheduler/shared";
import { supabase } from "./supabase";

export class ApiException extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.error || `HTTP ${status}`);
    this.name = "ApiException";
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- M7.1 active-org context -----------------------------------------------
// Super-admins and multi-org admins can switch which organization they're
// browsing. The choice is held here (mirrored to localStorage so it survives
// reloads) and sent as `X-Org-Slug` on every request. The Worker's scope
// helpers (lib/scope.ts) resolve the org from this header; single-org users
// send nothing and the Worker falls back to their default org.
const ORG_SLUG_KEY = "scheduler.active-org-slug";

function readStoredSlug(): string | null {
  try {
    return localStorage.getItem(ORG_SLUG_KEY);
  } catch {
    return null;
  }
}

let activeOrgSlug: string | null = readStoredSlug();

export function getActiveOrgSlug(): string | null {
  return activeOrgSlug;
}

export function setActiveOrgSlug(slug: string | null): void {
  activeOrgSlug = slug;
  try {
    if (slug) localStorage.setItem(ORG_SLUG_KEY, slug);
    else localStorage.removeItem(ORG_SLUG_KEY);
  } catch {
    // Private-mode / disabled storage: keep the in-memory value, which still
    // drives the header for the life of the tab.
  }
}

function orgHeader(): Record<string, string> {
  return activeOrgSlug ? { "X-Org-Slug": activeOrgSlug } : {};
}

// Prepend VITE_API_URL (set at build time) when the SPA and Worker live on
// different origins (production). Empty / unset leaves paths relative — the
// `vite dev` proxy handles dev forwarding to :8787.
const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export function apiUrl(path: string): string {
  return path.startsWith("http") ? path : `${API_BASE}${path}`;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...orgHeader(),
    ...(await authHeader()),
  };
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  // credentials: "omit" — we send JWT in Authorization header explicitly, and
  // the Worker's CSRF middleware checks Origin not cookies, so we don't need
  // to ship cookies to cross-origin requests.
  init.credentials = "omit";
  const res = await fetch(apiUrl(path), init);
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new ApiException(res.status, parsed as ApiError);
  }
  return parsed as T;
}

export const api = {
  health: () => request<HealthResponse>("GET", "/api/health"),
  me: () => request<MeResponse>("GET", "/api/me"),
  updateMe: (body: UpdateMeRequest) =>
    request<{ user: UserWithMemberships }>("PATCH", "/api/me", body),

  // v0.2.0 multi-tenancy.
  createOrg: (body: { name: string; slug: string }) =>
    request<{ organization: import("@scheduler/shared").Organization }>(
      "POST",
      "/api/orgs",
      body,
    ),
  listOrgs: () =>
    request<import("@scheduler/shared").ListOrganizationsResponse>(
      "GET",
      "/api/orgs",
    ),
  listMyOrgs: () =>
    request<import("@scheduler/shared").ListOrganizationsResponse>(
      "GET",
      "/api/orgs/mine",
    ),
  inviteAdmin: (body: import("@scheduler/shared").InviteAdminRequest) =>
    request<{ user_id: number; invited?: true; already_pending?: true; promoted?: true }>(
      "POST",
      "/api/admins/invite",
      body,
    ),
  inviteUser: (body: {
    email: string;
    display_name: string;
    role?: "employee" | "manager";
    sub_department_ids?: number[];
  }) =>
    request<{ user_id: number; email: string; invite_url: string }>(
      "POST",
      "/api/users/invite",
      body,
    ),

  listUsers: () => request<ListUsersResponse>("GET", "/api/users"),
  listUsersInSubDept: (subDeptId: number) =>
    request<ListUsersResponse>("GET", `/api/users?sub_department_id=${subDeptId}`),
  patchUser: (id: number, body: PatchUserRequest) =>
    request<{ user: UserWithMemberships }>("PATCH", `/api/users/${id}`, body),
  archiveUser: (id: number) =>
    request<{ ok: true }>("POST", `/api/users/${id}/archive`),

  listDepartments: (includeArchived = false) =>
    request<ListDepartmentsResponse>(
      "GET",
      `/api/departments${includeArchived ? "?include_archived=1" : ""}`,
    ),
  createDepartment: (body: CreateDepartmentRequest) =>
    request<{ department: Department }>("POST", "/api/departments", body),
  patchDepartment: (id: number, body: PatchDepartmentRequest) =>
    request<{ department: Department }>("PATCH", `/api/departments/${id}`, body),

  listSubDepartments: (departmentId?: number, includeArchived = false) => {
    const qs = new URLSearchParams();
    if (departmentId) qs.set("department_id", String(departmentId));
    if (includeArchived) qs.set("include_archived", "1");
    const q = qs.toString();
    return request<ListSubDepartmentsResponse>(
      "GET",
      `/api/sub-departments${q ? `?${q}` : ""}`,
    );
  },
  createSubDepartment: (body: CreateSubDepartmentRequest) =>
    request<{ sub_department: SubDepartment }>(
      "POST",
      "/api/sub-departments",
      body,
    ),
  patchSubDepartment: (id: number, body: PatchSubDepartmentRequest) =>
    request<{ sub_department: SubDepartment }>(
      "PATCH",
      `/api/sub-departments/${id}`,
      body,
    ),

  listShiftTemplates: (subDeptId?: number) =>
    request<ListShiftTemplatesResponse>(
      "GET",
      `/api/shift-templates${subDeptId ? `?sub_department_id=${subDeptId}` : ""}`,
    ),
  createShiftTemplate: (body: CreateShiftTemplateRequest) =>
    request<{ shift_template: ShiftTemplate }>(
      "POST",
      "/api/shift-templates",
      body,
    ),
  patchShiftTemplate: (id: number, body: PatchShiftTemplateRequest) =>
    request<{ shift_template: ShiftTemplate }>(
      "PATCH",
      `/api/shift-templates/${id}`,
      body,
    ),
  archiveShiftTemplate: (id: number) =>
    request<{ ok: true }>("POST", `/api/shift-templates/${id}/archive`),

  listShifts: (week: string, subDeptId: number) =>
    request<ListShiftsResponse>(
      "GET",
      `/api/shifts?week=${encodeURIComponent(week)}&sub_department_id=${subDeptId}`,
    ),
  patchShiftAssignments: (
    shiftId: number,
    body: PatchShiftAssignmentsRequest,
  ) =>
    request<{ shift: ShiftWithAssignments }>(
      "PATCH",
      `/api/shifts/${shiftId}/assignments`,
      body,
    ),

  generateSchedule: (body: GenerateScheduleRequest) =>
    request<GenerateScheduleResponse>("POST", "/api/schedule/generate", body),
  publishSchedule: (body: PublishScheduleRequest) =>
    request<{ ok: true }>("POST", "/api/schedule/publish", body),
  downloadScheduleCsv: async (week: string, subDeptId: number) => {
    const headers: Record<string, string> = {
      Accept: "text/csv",
      ...orgHeader(),
      ...(await authHeader()),
    };
    const res = await fetch(
      apiUrl(
        `/api/schedule/export?week=${encodeURIComponent(week)}&sub_department_id=${subDeptId}`,
      ),
      { method: "GET", headers, credentials: "omit" },
    );
    if (!res.ok) {
      const text = await res.text();
      try {
        throw new ApiException(res.status, JSON.parse(text) as ApiError);
      } catch {
        throw new Error(`Export failed: ${res.status}`);
      }
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `schedule-${subDeptId}-${week}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  listPreferences: (week: string) =>
    request<ListPreferencesResponse>(
      "GET",
      `/api/preferences?week=${encodeURIComponent(week)}`,
    ),
  putPreference: (shiftId: number, state: PreferenceState) =>
    request<{ preference: Preference }>(
      "PUT",
      `/api/preferences/${shiftId}`,
      { state },
    ),

  listDayUnavailability: (from: string, to: string) =>
    request<ListDayUnavailabilityResponse>(
      "GET",
      `/api/day-unavailability?from=${from}&to=${to}`,
    ),
  putDayUnavailability: (date: string, reason: string | null) =>
    request<{ entry: { user_id: number; date: string; reason: string | null } }>(
      "PUT",
      `/api/day-unavailability/${date}`,
      { reason },
    ),
  deleteDayUnavailability: (date: string) =>
    request<{ ok: true }>("DELETE", `/api/day-unavailability/${date}`),

  // Time-off
  listTimeOff: (userId?: number) =>
    request<ListTimeOffResponse>(
      "GET",
      `/api/time-off${userId !== undefined ? `?user_id=${userId}` : ""}`,
    ),
  createTimeOff: (body: CreateTimeOffRequest) =>
    request<{ time_off_request: TimeOffRequest }>("POST", "/api/time-off", body),
  decideTimeOff: (id: number, body: DecideTimeOffRequest) =>
    request<{ time_off_request: TimeOffRequest }>(
      "POST",
      `/api/time-off/${id}/decide`,
      body,
    ),

  // Change requests (drop / swap / modify)
  listChangeRequests: (status?: "pending" | "approved" | "denied") =>
    request<ListChangeRequestsResponse>(
      "GET",
      `/api/change-requests${status ? `?status=${status}` : ""}`,
    ),
  createChangeRequest: (body: CreateChangeRequest) =>
    request<{ change_request: ChangeRequest }>(
      "POST",
      "/api/change-requests",
      body,
    ),
  respondChangeRequest: (id: number, body: RespondChangeRequest) =>
    request<{ change_request: ChangeRequest }>(
      "POST",
      `/api/change-requests/${id}/respond`,
      body,
    ),
  decideChangeRequest: (id: number, body: DecideChangeRequest) =>
    request<{ change_request: ChangeRequest }>(
      "POST",
      `/api/change-requests/${id}/decide`,
      body,
    ),

  // Open shifts
  listOpenShifts: () =>
    request<ListOpenShiftsResponse>("GET", "/api/open-shifts"),
  postOpenShift: (shiftId: number) =>
    request<{ ok: true }>("POST", `/api/open-shifts/${shiftId}/post`),
  claimOpenShift: (shiftId: number) =>
    request<{ ok: true }>("POST", `/api/open-shifts/${shiftId}/claim`),
  confirmAssignment: (shiftId: number, body: ConfirmAssignmentRequest) =>
    request<{ ok: true }>("POST", `/api/open-shifts/${shiftId}/confirm`, body),

  // Notifications
  listNotifications: (onlyUnread = false) =>
    request<ListNotificationsResponse>(
      "GET",
      `/api/notifications${onlyUnread ? "?unread=1" : ""}`,
    ),
  markNotificationRead: (id: number) =>
    request<{ ok: true }>("POST", `/api/notifications/${id}/read`),
  markAllNotificationsRead: () =>
    request<{ ok: true }>("POST", "/api/notifications/read-all"),

  // Audit log (admin only)
  listAuditLog: (limit = 100) =>
    request<ListAuditLogResponse>("GET", `/api/audit-log?limit=${limit}`),

  // Billing entitlement (0009). GET is admin-visible; the two PATCHes are
  // super-admin only and enforced server-side.
  getBilling: () => request<GetBillingResponse>("GET", "/api/billing"),
  patchBilling: (body: PatchOrgBillingRequest) =>
    request<GetBillingResponse>("PATCH", "/api/billing", body),
  patchUserBilling: (id: number, body: PatchUserBillingRequest) =>
    request<GetBillingResponse>("PATCH", `/api/billing/users/${id}`, body),

  // System settings (admin only)
  getSettings: () => request<GetSettingsResponse>("GET", "/api/settings"),
  patchSettings: (body: PatchSettingsRequest) =>
    request<GetSettingsResponse>("PATCH", "/api/settings", body),

  // Holidays / org-wide closures (0008). GET is open to any auth'd user;
  // POST/PATCH/DELETE are admin-only and enforced server-side.
  listHolidays: (opts?: {
    from?: string;
    to?: string;
    sub_department_id?: number;
    include_archived?: boolean;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.from) qs.set("from", opts.from);
    if (opts?.to) qs.set("to", opts.to);
    if (opts?.sub_department_id !== undefined) {
      qs.set("sub_department_id", String(opts.sub_department_id));
    }
    if (opts?.include_archived) qs.set("include_archived", "1");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<ListHolidaysResponse>("GET", `/api/holidays${suffix}`);
  },
  createHoliday: (body: CreateHolidayRequest) =>
    request<{ holiday: Holiday }>("POST", "/api/holidays", body),
  patchHoliday: (
    id: number,
    body: PatchHolidayRequest & { archived?: boolean },
  ) => request<{ holiday: Holiday }>("PATCH", `/api/holidays/${id}`, body),
  deleteHoliday: (id: number) =>
    request<{ ok: true }>("DELETE", `/api/holidays/${id}`),
};
